import React from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Slide from "@mui/material/Slide";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const money = (value) => `$${(Number(value) || 0).toFixed(4)}`;

function HeadlineStat({ label, value, caption, highlight }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                flex: "1 1 220px",
                p: 2.5,
                borderRadius: 2,
                bgcolor: highlight ? "primary.main" : "background.paper",
                color: highlight ? "primary.contrastText" : "text.primary",
                borderColor: highlight ? "primary.main" : "divider",
            }}
        >
            <Typography
                variant="overline"
                sx={{ color: highlight ? "primary.contrastText" : "text.secondary", opacity: highlight ? 0.9 : 1 }}
            >
                {label}
            </Typography>
            <Typography variant="h4" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, my: 0.5 }}>
                {value}
            </Typography>
            {caption ? (
                <Typography
                    variant="caption"
                    sx={{ color: highlight ? "primary.contrastText" : "text.secondary", opacity: 0.85 }}
                >
                    {caption}
                </Typography>
            ) : null}
        </Paper>
    );
}

HeadlineStat.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    caption: PropTypes.string,
    highlight: PropTypes.bool,
};

function BreakdownPanel({ title, children }) {
    return (
        <Paper variant="outlined" sx={{ flex: "1 1 280px", borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.25, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    {title}
                </Typography>
            </Box>
            <Stack sx={{ px: 2, py: 0.5 }}>{children}</Stack>
        </Paper>
    );
}

BreakdownPanel.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

function BreakdownRow({ label, value, emphasize }) {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 2,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { borderBottom: 0 },
            }}
        >
            <Typography variant="body2" color={emphasize ? "text.primary" : "text.secondary"} fontWeight={emphasize ? 600 : 400}>
                {label}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={emphasize ? 700 : 600}
                sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
            >
                {value}
            </Typography>
        </Box>
    );
}

BreakdownRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
    emphasize: PropTypes.bool,
};

function RiderRatesTestDialog({
    open,
    onClose,
    title,
    isLoading,
    loadingMessage,
    bannerMessage,
    testKwhInput,
    onTestKwhChange,
    billingDate,
    onBillingDateChange,
    onRefreshTest,
    propertySelector,
    summary,
    riders,
    customerCharge,
    baseSummary,
    showCommonAreaTotal,
}) {
    const testKwh = Number(testKwhInput) || 0;
    const hasSummary = summary != null;
    const hasBase = baseSummary?.hasBase;
    const resolvedCustomerCharge = hasBase
        ? baseSummary.customerCharge
        : Number(customerCharge) || Number(summary?.utilCustomerCharge) || 0;
    const basePlusRidersTotal = hasSummary
        ? (summary.utilRidersTotal || 0) + resolvedCustomerCharge + (summary.utilEnergyCharge || 0)
        : 0;

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            fullWidth
            maxWidth="lg"
            onClose={onClose}
            aria-labelledby="rider-rates-test-dialog-title"
        >
            <DialogTitle id="rider-rates-test-dialog-title" sx={{ pb: 1 }}>
                {title}
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: "grey.50" }}>
                {bannerMessage ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {bannerMessage}
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", sm: "flex-end" }}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        {propertySelector ? (
                            <Box sx={{ flex: "1 1 280px", minWidth: 240 }}>{propertySelector}</Box>
                        ) : null}
                        {onBillingDateChange ? (
                            <TextField
                                id="rider-rates-test-billing-date"
                                label="Billing Date"
                                type="date"
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={billingDate || ""}
                                onChange={onBillingDateChange}
                                sx={{ width: { xs: "100%", sm: 180 } }}
                            />
                        ) : null}
                        <TextField
                            id="rider-rates-test-kwh"
                            label="kWh"
                            type="number"
                            variant="outlined"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={testKwhInput}
                            onChange={onTestKwhChange}
                            name="kwh"
                            sx={{ width: { xs: "100%", sm: 140 } }}
                        />
                        <Button variant="contained" onClick={onRefreshTest} color="secondary" sx={{ height: 40, px: 3 }}>
                            Refresh Test
                        </Button>
                    </Stack>
                </Paper>

                {isLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 6 }}>
                        <CircularProgress size={28} />
                        <Typography variant="h6" color="text.secondary">
                            {loadingMessage}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {hasSummary && (
                            <>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <HeadlineStat
                                        label="Total Billing Charge"
                                        value={money(summary.totalCharge)}
                                        caption="Base + Riders"
                                        highlight
                                    />
                                    <HeadlineStat
                                        label="Cost / kWh"
                                        value={testKwh > 0 ? money((summary.totalCharge || 0) / testKwh) : "N/A"}
                                        caption={`Total charge ÷ ${testKwh} kWh`}
                                    />
                                    {showCommonAreaTotal ? (
                                        <HeadlineStat
                                            label="Complete Bill"
                                            value={money(
                                                (summary.totalCharge || 0) + (summary.commonAreaTotal || 0)
                                            )}
                                            caption="Includes common area charges"
                                        />
                                    ) : null}
                                </Stack>

                                {hasBase ? (
                                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                        <BreakdownPanel title="Base Charges">
                                            <BreakdownRow label="Customer charge" value={money(resolvedCustomerCharge)} />
                                            <BreakdownRow label="Energy charge" value={money(summary.utilEnergyCharge)} />
                                            <BreakdownRow
                                                label="Base total"
                                                value={money(resolvedCustomerCharge + (summary.utilEnergyCharge || 0))}
                                                emphasize
                                            />
                                        </BreakdownPanel>

                                        <BreakdownPanel title="Rider Totals">
                                            <BreakdownRow label="Riders total" value={money(summary.utilRidersTotal)} />
                                            <BreakdownRow label="Generation" value={money(summary.utilGenerationTotal)} />
                                            <BreakdownRow label="Transmission" value={money(summary.utilTransmissionTotal)} />
                                            <BreakdownRow label="Distribution" value={money(summary.utilDistributionTotal)} />
                                            <BreakdownRow label="Base + riders" value={money(basePlusRidersTotal)} emphasize />
                                            {showCommonAreaTotal ? (
                                                <BreakdownRow label="Common area" value={money(summary.commonAreaTotal)} />
                                            ) : null}
                                        </BreakdownPanel>
                                    </Stack>
                                ) : (
                                    <Alert severity="info">
                                        No base charges for this vendor. Billing is calculated from riders only.
                                    </Alert>
                                )}

                                {!hasBase && hasSummary ? (
                                    <BreakdownPanel title="Rider Totals">
                                        <BreakdownRow label="Riders total" value={money(summary.utilRidersTotal)} />
                                        <BreakdownRow label="Generation" value={money(summary.utilGenerationTotal)} />
                                        <BreakdownRow label="Transmission" value={money(summary.utilTransmissionTotal)} />
                                        <BreakdownRow label="Distribution" value={money(summary.utilDistributionTotal)} emphasize />
                                        {showCommonAreaTotal ? (
                                            <BreakdownRow label="Common area" value={money(summary.commonAreaTotal)} />
                                        ) : null}
                                    </BreakdownPanel>
                                ) : null}
                            </>
                        )}

                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                            <Box sx={{ px: 2, py: 1.25, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Rider Breakdown
                                </Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small" aria-label="Rider breakdown">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "grey.50" }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Rider Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Value Type</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                                Rider Rate
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                                Cost on Bill
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(riders || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No rider rates to display.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            riders.map((rider, index) => (
                                                <TableRow
                                                    key={rider.uuid || `${rider.riderName}-${index}`}
                                                    hover
                                                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                                                >
                                                    <TableCell>{rider.riderName}</TableCell>
                                                    <TableCell>{rider.riderType}</TableCell>
                                                    <TableCell>
                                                        {[rider.rateType, rider.rateBasis === "Base" ? "Base (Dist)" : rider.rateBasis]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                        {rider.effectiveRate}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                                                        {money(rider.riderAmount)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}

RiderRatesTestDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    isLoading: PropTypes.bool,
    loadingMessage: PropTypes.string,
    bannerMessage: PropTypes.string,
    testKwhInput: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onTestKwhChange: PropTypes.func.isRequired,
    billingDate: PropTypes.string,
    onBillingDateChange: PropTypes.func,
    onRefreshTest: PropTypes.func.isRequired,
    propertySelector: PropTypes.node,
    summary: PropTypes.object,
    riders: PropTypes.array,
    customerCharge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    baseSummary: PropTypes.shape({
        hasBase: PropTypes.bool,
        customerCharge: PropTypes.number,
        energyChargeRate: PropTypes.number,
        energyCharge: PropTypes.number,
        baseTotal: PropTypes.number,
    }),
    showCommonAreaTotal: PropTypes.bool,
};

RiderRatesTestDialog.defaultProps = {
    title: "Test Rider Rates Output",
    isLoading: false,
    loadingMessage: "Loading rider rates…",
    summary: null,
    riders: [],
    customerCharge: 0,
    showCommonAreaTotal: false,
};

export default RiderRatesTestDialog;
